import { Injectable } from '@angular/core';

import { Web3Service } from './web3.service';

import { stringify } from 'svgson';
import tinyColor from 'tinycolor2';

import { Attribute } from '@/models/graph';
import { TraitCountPipe } from '@/pipes/trait-count.pipe';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root'
})

export class PunkCardService {

  constructor(
    private dataSvc: DataService,
    private traitCountPipe: TraitCountPipe,
    private http: HttpClient,
  ) {}

  async createImage(
    ctx: CanvasRenderingContext2D,
    punkSVGData: string,
    attributes: Attribute[],
    punkId: string,
    type?: 'sales' | 'bids' | 'offers'
  ): Promise<{ base64: string, color: string }> {

    // registerFont(path.join(__dirname, '../static/retro-computer.ttf'), { family: 'RetroComputer' });

    const fontLoad = new FontFace('RetroComputer', 'url(/assets/fonts/retro-computer.ttf)');

    const canvasWidth = 1200;
    const canvasHeight = 1200;

    const punkWidth = canvasWidth / 2;
    const punkHeight = canvasHeight / 2;

    const bleed = 30 * 2;

    const lowerThird = ((canvasHeight / 3) * 2) - (bleed * 2) + 20;

    if (!ctx) throw new Error('Could not create canvas context.');

    ctx.fillStyle = '#131415';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    const color = 'rgba(99, 133, 150, 1)';

    const font = (size: number) => `normal ${size}px RetroComputer`;

    ctx.fillStyle = color;
    ctx.fillRect(bleed, bleed, canvasWidth - (bleed * 2), lowerThird);

    const img = new Image();
    img.onload = () => ctx.drawImage(
      img,
      (canvasWidth / 2) - (punkWidth / 2) - (bleed / 2),
      lowerThird - punkHeight + bleed,
      punkWidth,
      punkHeight,
    );
    img.onerror = err => { throw err };

    const dataUrl = 'data:image/svg+xml;utf8,' + encodeURIComponent(punkSVGData);
    img.width = punkWidth / 10;
    img.height = punkHeight / 10;
    img.src = dataUrl;

    // Line 1 (left side)
    const line1 = 'CryptoPunk';
    const line1Pos = lowerThird + (bleed * 2);
    ctx.textBaseline = 'top';
    ctx.font = font(36);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(
      line1,
      bleed,
      line1Pos
    );

    // Line 2 (left side)
    ctx.textBaseline = 'top';
    ctx.font = font(120);
    ctx.fillStyle = color || '#FF04B4';
    ctx.fillText(
      punkId,
      bleed - 5,
      line1Pos + 50
    );

    // Line 3 (right side)
    
    const sex = attributes[0];
    
    const line3Pos = lowerThird + (bleed * 2);
    const line3_1 = `${sex.v} punks`;
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(
      line3_1,
      canvasWidth - bleed,
      line3Pos
    );

    const line3_1Width = (ctx.measureText(line3_1).width + 8);
    const traitCount = this.traitCountPipe.transform(attributes[0].v);

    const line3_2 = `${traitCount}`;
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = color || '#ff04b4';
    ctx.fillText(
      line3_2,
      canvasWidth - bleed - line3_1Width,
      line3Pos
    );

    const line3_2Width = (ctx.measureText(line3_2).width + 8);

    const line3_3 = `One of`;
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(
      line3_3,
      canvasWidth - bleed - line3_1Width - line3_2Width,
      line3Pos
    );

    const line4Pos = lowerThird + (bleed * 2) + 40;
    const line4_1 = `Trait${attributes.length > 2 ? 's' : ''}`
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(
      line4_1,
      canvasWidth - bleed,
      line4Pos
    );

    const line4_1Width = (ctx.measureText(line4_1).width + 8);

    const line4_2 = `${attributes.length - 2}`;
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = color || '#ff04b4';
    ctx.fillText(
      line4_2,
      canvasWidth - bleed - line4_1Width,
      line4Pos
    );

    let traitsPos = line4Pos + 30;
    for (const attr of attributes) {

      if (attr.k !== 'Skintone' && attr.k !== 'Sex') {

        const val = this.traitCountPipe.transform(attr.v);
        const lineTrait_1 = `${(+val * 100) / 10000}%`;
        ctx.font = font(22);
        ctx.textAlign = 'right';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(
          lineTrait_1,
          canvasWidth - bleed,
          traitsPos
        );

        const lineTrait_1Width = (ctx.measureText(lineTrait_1).width + 8);

        const lineTrait_2 = `${attr.v}`;
        ctx.font = font(20);
        ctx.textAlign = 'right';
        ctx.fillStyle = color || '#FF04B4';
        ctx.fillText(
          lineTrait_2,
          canvasWidth - bleed - lineTrait_1Width,
          traitsPos
        );
      }

      traitsPos = traitsPos + 30;
    }

    const imgUrl = '/assets/logo.svg';
    const logo = await firstValueFrom(this.http.get(imgUrl, { responseType: 'text' }));
    const logoImg = new Image();
    logoImg.onload = () => ctx.drawImage(
      logoImg,
      bleed,
      canvasHeight - bleed - 20
    );
    logoImg.onerror = err => { throw err };
    logoImg.src = 'data:image/svg+xml;utf8,' + encodeURIComponent(logo);

    // const buffer = canvas.toBuffer('image/png');

    return {
      base64: '', // buffer.toString('base64'),
      color: tinyColor(color).toHex()
    };
  }

  // Get the "brightest" color from the punk data
  getColor(punkData: string): string {
    const colorGroups: any = {};
    const punkArr = punkData?.replace('0x', '').match(/.{1,8}/g) as string[];
  
    punkArr.map((color: any) => {
      if (!colorGroups[color]) colorGroups[color] = 1;
      colorGroups[color]++;
    });
  
    const hslColors = Object.keys(colorGroups).map((key: any) => tinyColor(key).toHsl());
    const brightest = hslColors.sort((a: any, b: any) => {
      if (b.s !== a.s) return b.s - a.s;
      return b.l - a.l;
    })[0];
  
    return tinyColor(brightest).setAlpha(.15).toRgbString();
  }
  
  // Create SVG from punk data
  // async createPunkSvg(punkData: string, width: number, height: number): Promise<any> {
    
  //   const punkArr = punkData?.replace('0x', '').match(/.{1,8}/g) as string[];

  //   console.log(punkArr)

  //   const svg = {
  //     name: 'svg',
  //     type: 'element',
  //     value: '',
  //     attributes: {
  //       xmlns: 'http://www.w3.org/2000/svg',
  //       version: '1.2',
  //       viewBox: '0 0 24 24',
  //       width: width.toString(),
  //       height: height.toString()
  //     },
  //     children: punkArr.map((res, i) => {
  //       return {
  //         name: 'rect',
  //         type: 'element',
  //         value: '',
  //         attributes: {
  //           x: `${(i % 24)}`,
  //           y: `${Math.floor(i / 24)}`,
  //           width: '1',
  //           height: '1',
  //           'shape-rendering': 'crispEdges',
  //           fill: `#${res}`
  //         },
  //         children: []
  //       }
  //     })
  //   };

  //   return stringify(svg);
  // }

}